import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/store";
import {
  clearOAuthState,
  exchangeCodeForSessionSafe,
  getOAuthError,
  hasOAuthError,
  logOAuthConfig,
} from "@/utils/auth";
import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    initialize,
    setUser,
    setSession,
    setProfile,
    setWorkspaces,
    setCurrentWorkspaceId,
    initialized,
  } = useAuthStore();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const timeoutRef = useRef(null);
  const redirectAttempted = useRef(false);

  useEffect(() => {
    // Prevent duplicate processing
    if (redirectAttempted.current) {
      return;
    }

    // Check if we've already processed this callback recently
    const lastProcessedTime = sessionStorage.getItem("auth_callback_timestamp");
    const now = Date.now();
    if (lastProcessedTime && now - parseInt(lastProcessedTime) < 5000) {
      return;
    }

    // Clean up URL if we're on the wrong OAuth callback path or have unwanted parameters
    const currentPath = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);

    // Check if we're on /oauth/callback instead of /auth/callback
    if (currentPath === "/oauth/callback") {
      console.log("Redirecting from /oauth/callback to /auth/callback");
      const newUrl = window.location.href.replace(
        "/oauth/callback",
        "/auth/callback"
      );
      window.history.replaceState({}, document.title, newUrl);
    }

    // Remove mobile_app parameter if present
    if (urlParams.has("mobile_app")) {
      console.log("Removing mobile_app parameter from URL");
      urlParams.delete("mobile_app");
      const newSearch = urlParams.toString();
      const newUrl = `${window.location.origin}${window.location.pathname}${newSearch ? "?" + newSearch : ""}${window.location.hash}`;
      window.history.replaceState({}, document.title, newUrl);
    }

    // Set a safety timeout to prevent infinite loading
    timeoutRef.current = setTimeout(() => {
      redirectAttempted.current = true;
      window.location.href = "/auth/login?timeout=true";
    }, 15000);

    const handleAuthCallback = async () => {
      try {
        setIsProcessing(true);

        // Check for OAuth errors in URL
        if (hasOAuthError()) {
          const oauthError = getOAuthError();
          console.error("OAuth error detected:", oauthError);
          await clearOAuthState();
          throw new Error(
            `OAuth Error: ${oauthError.error}: ${oauthError.description}`
          );
        }

        // Log OAuth config for debugging
        logOAuthConfig();

        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const authCode = urlParams.get("code");
        const tokenHash = urlParams.get("token_hash");
        const type = urlParams.get("type");

        // Check for URL hash fragments (implicit flow magic links)
        const hashParams = new URLSearchParams(
          window.location.hash.substring(1)
        );
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");

        if (authCode) {
          // First, check if we already have a valid session (in case automatic detection worked)
          const {
            data: { session: existingSession },
          } = await supabase.auth.getSession();

          if (existingSession && existingSession.user) {
            // Store session data in auth store
            setUser(existingSession.user);
            setSession(existingSession);

            // Set a flag to indicate we've completed processing
            try {
              sessionStorage.setItem("auth_callback_processed", "true");
              sessionStorage.setItem(
                "auth_callback_timestamp",
                Date.now().toString()
              );
            } catch (e) {
              console.warn("Failed to set auth_callback_processed flag:", e);
            }

            // Check profile and redirect to appropriate page
            await completeAuthentication(existingSession);
            return;
          }

          // No existing session, proceed with manual code exchange
          const { success, session, error, isRateLimit, isFlowStateError } =
            await exchangeCodeForSessionSafe(supabase, authCode);

          if (!success) {
            if (isRateLimit) {
              console.error("Rate limit detected");
              redirectAttempted.current = true;
              navigate("/auth/login", {
                replace: true,
                state: {
                  error:
                    "Authentication rate limit reached. Please try again shortly.",
                },
              });
              return;
            }

            if (isFlowStateError) {
              console.error("Flow state error detected");
              await clearOAuthState();

              // Before failing, check one more time if we have a session
              // (in case the automatic detection succeeded but we got a stale error)
              const {
                data: { session: finalCheckSession },
              } = await supabase.auth.getSession();

              if (finalCheckSession && finalCheckSession.user) {
                setUser(finalCheckSession.user);
                setSession(finalCheckSession);
                await completeAuthentication(finalCheckSession);
                return;
              }

              redirectAttempted.current = true;
              navigate("/auth/login", {
                replace: true,
                state: {
                  error:
                    "Your authentication session expired. Please sign in again.",
                },
              });
              return;
            }

            throw error || new Error("Failed to exchange code for session");
          }

          // Store session data in auth store
          setUser(session.user);
          setSession(session);

          // Set a flag to indicate we've completed processing
          try {
            sessionStorage.setItem("auth_callback_processed", "true");
            sessionStorage.setItem(
              "auth_callback_timestamp",
              Date.now().toString()
            );
          } catch (e) {
            console.warn("Failed to set auth_callback_processed flag:", e);
          }

          // Check profile and redirect to appropriate page
          await completeAuthentication(session);
        }
        // Handle magic link PKCE flow (token_hash + type)
        else if (tokenHash && type) {
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: type,
          });

          if (error) {
            console.error("Magic link verification error:", error);

            // Handle specific magic link errors
            if (
              error.message?.includes("expired") ||
              error.message?.includes("invalid")
            ) {
              redirectAttempted.current = true;
              navigate("/auth/login", {
                replace: true,
                state: {
                  error:
                    "Your magic link has expired or is invalid. Please request a new one.",
                },
              });
              return;
            }

            throw error;
          }

          if (data?.user && data?.session) {
            // Store session data in auth store
            setUser(data.user);
            setSession(data.session);

            // Set a flag to indicate we've completed processing
            try {
              sessionStorage.setItem("auth_callback_processed", "true");
              sessionStorage.setItem(
                "auth_callback_timestamp",
                Date.now().toString()
              );
            } catch (e) {
              console.warn("Failed to set auth_callback_processed flag:", e);
            }

            // Check profile and redirect to appropriate page
            await completeAuthentication(data.session);
          } else {
            throw new Error(
              "Magic link verification did not return session data"
            );
          }
        }
        // Handle implicit flow magic links (access_token in hash)
        else if (accessToken) {
          // For implicit flow, we need to set the session manually
          // First, let's try to get the session using the access token
          const {
            data: { session },
            error,
          } = await supabase.auth.getSession();

          if (session) {
            // Store session data in auth store
            setUser(session.user);
            setSession(session);

            // Set a flag to indicate we've completed processing
            try {
              sessionStorage.setItem("auth_callback_processed", "true");
              sessionStorage.setItem(
                "auth_callback_timestamp",
                Date.now().toString()
              );
            } catch (e) {
              console.warn("Failed to set auth_callback_processed flag:", e);
            }

            // Check profile and redirect to appropriate page
            await completeAuthentication(session);
          } else {
            console.warn("Could not retrieve session from access token");

            // Try to set the session using the tokens from the hash
            if (refreshToken) {
              const { data, error } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });

              if (error) {
                console.error("Error setting session from hash tokens:", error);
                throw error;
              }

              if (data?.session) {
                // Store session data in auth store
                setUser(data.session.user);
                setSession(data.session);

                // Check profile and redirect to appropriate page
                await completeAuthentication(data.session);
              } else {
                throw new Error("Failed to set session from hash tokens");
              }
            } else {
              throw new Error("No refresh token found in URL hash");
            }
          }
        }
        // No auth parameters found - check for existing session
        else {
          console.warn("Auth callback accessed without auth parameters");

          // Check if we already have a session
          const {
            data: { session },
          } = await supabase.auth.getSession();

          if (session) {
            await completeAuthentication(session);
          } else {
            // No session and no auth parameters, redirect to login
            redirectAttempted.current = true;
            navigate("/auth/login", { replace: true });
          }
        }
      } catch (error) {
        console.error("Error in auth callback:", error);
        setError(error.message || "Authentication failed");
        redirectAttempted.current = true;

        // Redirect to login with error
        navigate("/auth/login", {
          replace: true,
          state: {
            error: "Authentication failed. Please try again.",
            errorDetails: error.message,
          },
        });
      } finally {
        // Cancel the safety timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setIsProcessing(false);
      }
    };

    // Helper function to complete authentication and redirect
    const completeAuthentication = async (session) => {
      try {
        // Get user profile with better error handling
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single();

        if (profileError) {
          console.warn(
            "🔑 Error fetching profile during auth callback:",
            profileError
          );
          // Continue without profile - we'll create one if needed
        }

        // CRITICAL: Set all auth data in the correct order before initializing
        setUser(session.user);
        setSession(session);
        if (profile) {
          // Use the enhanced setProfile method to ensure both profile and profileData are set
          useAuthStore.getState().setProfile(profile);

          // Verify the profile and profileData were set correctly
          const { profile: setProfileResult, profileData } =
            useAuthStore.getState();

          // CRITICAL: Initialize the app immediately after we have the profile
          await initialize();
        }

        // CRITICAL: Set flags for forced reinitialization on the chat page
        try {
          localStorage.setItem(
            "auth_redirect_completed",
            Date.now().toString()
          );
          localStorage.setItem("force_initialize_after_oauth", "true");

          // Clear any previous initialization state
          sessionStorage.removeItem("auth_init_cache");
          sessionStorage.removeItem("auth_init_timestamp");
        } catch (e) {
          console.warn("Failed to set auth redirect flags:", e);
        }

        // CRITICAL: Explicitly fetch and set workspace data for this user
        // This must happen before other initialization steps
        const workspaceResult = await initializeWorkspace(session.user.id);

        if (!workspaceResult.success) {
          console.error(
            "Failed to initialize workspace:",
            workspaceResult.error
          );

          // Try one more time with a slightly different approach as fallback

          // Get the workspaces after initialization (already called above)
          const { workspaces, currentWorkspaceId } = useAuthStore.getState();

          if (!currentWorkspaceId && workspaces && workspaces.length > 0) {
            // Set current workspace ID manually
            const workspace =
              workspaces.find((w) => w.is_personal) || workspaces[0];
            setCurrentWorkspaceId(workspace.id);
          }
        }

        // Clear all previous initialization flags to ensure fresh initialization
        try {
          // Reset all auth and initialization flags
          sessionStorage.removeItem("auth_init_cache");
          sessionStorage.removeItem("auth_init_timestamp");
          sessionStorage.removeItem("auth_init_error");
          sessionStorage.removeItem("auth_callback_completed");

          // Force clear assistants initialized state to ensure it runs again
          useAuthStore.getState().setAssistantsInitialized(false);
        } catch (storageError) {
          console.warn("Failed to clear initialization flags:", storageError);
        }

        // Check if onboarding is needed
        const needsOnboarding = !profile || !profile.onboarding_completed;

        if (needsOnboarding) {
          // Create a new profile if it doesn't exist
          if (!profile) {
            try {
              const { data: newProfile } = await supabase
                .from("profiles")
                .insert([
                  {
                    id: session.user.id,
                    email: session.user.email,
                    full_name: session.user.user_metadata?.full_name || "",
                    avatar_url: session.user.user_metadata?.avatar_url || "",
                    onboarding_completed: false,
                    settings: {
                      profile: {},
                      account: {},
                      personalization: {},
                      memory: {},
                      dataControls: {},
                      subscription: {},
                      beta: {},
                      system: {},
                    },
                  },
                ])
                .select()
                .single();

              if (newProfile) {
                // Use the enhanced setProfile method to ensure both profile and profileData are set
                useAuthStore.getState().setProfile(newProfile);

                // Verify the new profile was set correctly
                const { profile: newProfileResult, profileData } =
                  useAuthStore.getState();
              }
            } catch (err) {
              console.error("Error creating profile:", err);
            }
          }

          // Verify workspace ID is set before proceeding
          const { currentWorkspaceId, workspaces } = useAuthStore.getState();

          // Add a delay before redirect to ensure state is properly saved
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Redirect to onboarding
          redirectAttempted.current = true;
          navigate("/auth/onboarding", { replace: true });
        } else {
          // User has completed onboarding, redirect to chat

          // Verify workspace ID is set
          const { currentWorkspaceId, workspaces } = useAuthStore.getState();

          // If no workspace ID but we have workspaces, set the first one
          if (!currentWorkspaceId && workspaces && workspaces.length > 0) {
            const workspace =
              workspaces.find((w) => w.is_personal) || workspaces[0];
            setCurrentWorkspaceId(workspace.id);
          }

          // Add a slight delay before redirecting
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Verify key data is loaded before redirect
          const state = useAuthStore.getState();

          // Redirect to chat
          redirectAttempted.current = true;
          navigate("/chat/new", { replace: true });
        }
      } catch (error) {
        console.error("Error in completeAuthentication:", error);
        redirectAttempted.current = true;
        navigate("/chat/new", { replace: true }); // Fallback
      }
    };

    // Helper function to initialize workspace
    const initializeWorkspace = async (userId) => {
      try {
        // First check for existing workspaces with improved query
        const { data: workspaces, error: workspaceError } = await supabase
          .from("workspaces")
          .select("*")
          .eq("user_id", userId);

        if (workspaceError) {
          console.error("Error fetching workspaces:", workspaceError);
          throw workspaceError;
        }

        if (workspaces && workspaces.length > 0) {
          // Set workspaces in auth store
          setWorkspaces(workspaces);

          // Set current workspace - prefer personal workspace if it exists
          const personalWorkspace =
            workspaces.find((w) => w.is_personal) || workspaces[0];

          setCurrentWorkspaceId(personalWorkspace.id);

          // Verify the workspace was set correctly
          setTimeout(() => {
            const { currentWorkspaceId } = useAuthStore.getState();

            if (!currentWorkspaceId) {
              useAuthStore
                .getState()
                .setCurrentWorkspaceId(personalWorkspace.id);
            }
          }, 100);

          return { success: true, workspace: personalWorkspace };
        }

        // Create personal workspace if none exist
        const { data: newWorkspace, error: createError } = await supabase
          .from("workspaces")
          .insert([
            {
              user_id: userId,
              owner_id: userId, // Also set owner_id
              name: "Personal Project",
              is_personal: true,
              settings: {},
              workspace_members: [userId],
              metadata: {
                created_at: new Date().toISOString(),
                created_by: userId,
              },
            },
          ])
          .select()
          .single();

        if (createError) {
          console.error("Error creating personal workspace:", createError);
          throw createError;
        }

        if (newWorkspace) {
          // Create workspace_members entry for the user
          const { error: memberError } = await supabase
            .from("workspace_members")
            .insert({
              user_id: userId,
              workspace_id: newWorkspace.id,
              role: "owner",
              permissions: {
                workspace: {
                  edit: true,
                  delete: true,
                  invite_members: true,
                  manage_roles: true,
                },
                files: {
                  upload: true,
                  delete: true,
                  share: true,
                },
                memories: {
                  create: true,
                  edit: true,
                  delete: true,
                },
                settings: {
                  view: true,
                  edit: true,
                },
              },
            });

          if (memberError) {
            console.error(
              "Error creating workspace member entry:",
              memberError
            );
            // Continue despite error - we have the workspace
          }

          setWorkspaces([newWorkspace]);
          setCurrentWorkspaceId(newWorkspace.id);

          // Double check the workspace was set correctly
          setTimeout(() => {
            const { currentWorkspaceId } = useAuthStore.getState();

            if (currentWorkspaceId !== newWorkspace.id) {
              useAuthStore.getState().setCurrentWorkspaceId(newWorkspace.id);
            }
          }, 100);

          return { success: true, workspace: newWorkspace };
        }

        return { success: false, error: "Failed to create workspace" };
      } catch (error) {
        console.error("Error initializing workspace:", error);
        return { success: false, error };
      }
    };

    handleAuthCallback();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    navigate,
    initialize,
    location,
    setUser,
    setSession,
    setProfile,
    setWorkspaces,
    setCurrentWorkspaceId,
    initialized,
  ]);

  // Show loader while processing authentication
  if (isProcessing) {
    return (
      <div className="h-screen flex flex-col items-center justify-center">
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-background/20 backdrop-blur-[2px]">
          <div className="w-8 h-8 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return <Outlet />;
}
